import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { type ChangeEvent, type ComponentProps, type FormEvent, useCallback, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { ConsentCheck } from "@/features/legal/components/consent-check";
import { LegalInline } from "@/features/legal/components/legal-markdown";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { isAdultBirthDate, LegalConsentKind } from "@complete-athlete/enums";

const legalConsents = [LegalConsentKind.TERMS];

export function SignupForm({ className, ...props }: ComponentProps<"form">) {
  const { signUp, signupResponse, signInWithGoogle, googleSubmitting } = useAuth();
  const { redirect } = useSearch({ from: "/(public)/signup/" });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [consentMissing, setConsentMissing] = useState(false);

  const navigate = useNavigate();

  const handleFirstNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  }, []);

  const handleLastNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  }, []);

  const [birthDate, setBirthDate] = useState("");
  const [birthDateError, setBirthDateError] = useState<string | null>(null);

  // Checked before Google too, so nobody leaves for a sign-up that cannot end in an account.
  const checkBirthDate = useCallback(() => {
    const problem = !birthDate
      ? m.signup_birth_date_required()
      : isAdultBirthDate(birthDate)
        ? null
        : m.signup_birth_date_under_18();
    setBirthDateError(problem);
    return !problem;
  }, [birthDate]);

  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  const handlePasswordChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleGoogleSignIn = useCallback(() => {
    setConsentMissing(!acceptedTerms);
    if (!checkBirthDate() || !acceptedTerms) return;
    signInWithGoogle("signup", legalConsents, birthDate);
  }, [acceptedTerms, birthDate, checkBirthDate, signInWithGoogle]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setConsentMissing(!acceptedTerms);
      if (!checkBirthDate() || !acceptedTerms) return;
      const { data, error } = await signUp({ email, password, firstName, lastName, legalConsents, birthDate });

      if (data) {
        toast.success(m.auth_signup_toast_verify_email_title(), {
          description: m.auth_signup_toast_verify_email_description(),
        });
        navigate({ to: "/onboarding", search: redirect ? { redirect } : {} });
      }

      if (error) {
        toast.error(error.message);
      }
    },
    [acceptedTerms, birthDate, checkBirthDate, email, firstName, lastName, navigate, password, redirect, signUp],
  );

  return (
    <form {...props} className={cn("flex flex-col gap-6", className)} onSubmit={handleSubmit}>
      <FieldGroup>
        <div className="grid gap-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{m.auth_signup_title()}</h1>
          <p className="text-muted-foreground text-sm">{m.auth_signup_description()}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="firstName">{m.auth_field_first_name_label()}</FieldLabel>
            <Input
              id="firstName"
              type="text"
              placeholder={m.auth_field_first_name_placeholder()}
              required
              value={firstName}
              onChange={handleFirstNameChange}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="lastName">{m.auth_field_last_name_label()}</FieldLabel>
            <Input
              id="lastName"
              type="text"
              placeholder={m.auth_field_last_name_placeholder()}
              required
              value={lastName}
              onChange={handleLastNameChange}
            />
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor="email">{m.auth_field_email_label()}</FieldLabel>
          <Input
            id="email"
            type="email"
            placeholder={m.auth_field_email_placeholder()}
            required
            value={email}
            onChange={handleEmailChange}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="password">{m.auth_field_password_label()}</FieldLabel>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="********"
              inputClassName="pr-10"
              required
              value={password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? m.auth_password_hide() : m.auth_password_show()}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          <FieldDescription>{m.auth_signup_password_hint()}</FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="birthDate">{m.signup_birth_date_label()}</FieldLabel>
          <Input
            id="birthDate"
            type="date"
            autoComplete="bday"
            required
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
          />
          {birthDateError ? (
            <p role="alert" className="text-destructive text-[13px]">
              {birthDateError}
            </p>
          ) : (
            <FieldDescription>{m.signup_birth_date_hint()}</FieldDescription>
          )}
        </Field>
        <Field>
          <div className="grid gap-3">
            <ConsentCheck checked={acceptedTerms} onChange={setAcceptedTerms}>
              <LegalInline source={m.signup_consent_terms()} />
            </ConsentCheck>
            {consentMissing ? (
              <p role="alert" className="text-destructive text-[13px]">
                {m.signup_consent_required()}
              </p>
            ) : null}
          </div>
        </Field>
        <Field>
          <Button type="submit" disabled={signupResponse.submitting}>
            {signupResponse.submitting && <Loader2 className="size-4 animate-spin" />}
            {m.auth_signup_submit()}
          </Button>
        </Field>
        <div className="flex items-center gap-4 text-sm" data-slot="field-separator">
          <Separator className="flex-1" />
          <span className="text-muted-foreground">{m.auth_or_continue_with()}</span>
          <Separator className="flex-1" />
        </div>
        <Field>
          <Button variant="outline" type="button" onClick={handleGoogleSignIn} disabled={googleSubmitting}>
            {googleSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
            )}
            {m.auth_signup_google()}
          </Button>
          <FieldDescription>
            {m.auth_signup_have_account()}{" "}
            <Link to="/login" className="text-primary font-medium underline-offset-4 hover:underline">
              {m.auth_signup_login_link()}
            </Link>
          </FieldDescription>
          <FieldDescription>
            {m.auth_legal_prefix()}{" "}
            <Link to="/terms" className="underline underline-offset-4">
              {m.auth_legal_terms()}
            </Link>{" "}
            {m.auth_legal_and()}{" "}
            <Link to="/privacy" className="underline underline-offset-4">
              {m.auth_legal_privacy_accepted()}
            </Link>
            .
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
}
